'use client';

import AppBar from '@enlighten/design-system/dist/AppBar';
import Button from '@enlighten/design-system/dist/Button';
import { warmGrey } from '@enlighten/design-system/dist/colors';
import Drawer from '@enlighten/design-system/dist/Drawer';
import Grid from '@enlighten/design-system/dist/Grid';
import List from '@enlighten/design-system/dist/List';
import ListItem from '@enlighten/design-system/dist/ListItem';
import ListItemButton from '@enlighten/design-system/dist/ListItemButton';
import type { Theme } from '@enlighten/design-system/dist/styles';
import Typography from '@enlighten/design-system/dist/Typography';
import useMediaQuery from '@enlighten/design-system/dist/useMediaQuery';
import LaunchIcon from '@mui/icons-material/Launch';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import CloseIcon from '@/assets/images/close.svg';
import HamburgerIcon from '@/assets/images/hamburger.svg';
import KrIcon from '@/assets/images/intl/kr-icon.svg';
import ArrowRightIcon from '@/assets/images/right.svg';
import { enNavigationList } from '@/constants/navigation';
import { fonts } from '@/styles/fonts';
import variables from '@/styles/variables.module.scss';

export default function MobileHeader() {
	const [openDrawer, setOpenDrawer] = useState<boolean>(false);

	const pathname = usePathname();
	const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));

	useEffect(() => {
		setOpenDrawer(false);
	}, [isMobile, pathname]);

	return (
		<AppBar
			className="gnb"
			sx={{
				display: 'flex',
				alignItems: 'center',
				bgcolor: 'common.white',
				height: 64,
				zIndex: 998,
				boxShadow: '0 4px 28px #0000001A',
				transition: 'all 0.3s ease',
			}}
		>
			<Grid container flexWrap="nowrap" justifyContent="space-between" alignItems="center" width="100%" height="100%" padding="0 24px">
				<Grid display="flex" justifyContent="space-between" alignItems="center" height="100%">
					<Link href="/en/about" aria-label="ENlighten" style={{ display: 'flex', alignItems: 'center', width: 'fit-content', height: '100%' }}>
						<Grid
							className="logo"
							width={146}
							height={24}
							sx={{
								backgroundImage: 'url(/logo.svg)',
								backgroundRepeat: 'no-repeat',
								backgroundSize: 'cover',
								filter: 'none',
							}}
						/>
					</Link>
					<Button
						title="hamburger"
						variant="text"
						onClick={() => {
							setOpenDrawer((open) => !open);
						}}
						disableTouchRipple
						sx={{
							position: 'absolute',
							right: 0,
							minWidth: 0,
							height: '100%',
							paddingX: 4,
							':hover': {
								bgcolor: 'transparent',
							},
						}}
					>
						{openDrawer ? <CloseIcon width={24} height={24} /> : <HamburgerIcon width={24} height={24} />}
					</Button>
				</Grid>
				<Drawer
					anchor="right"
					open={openDrawer}
					hideBackdrop
					onClose={() => {
						setOpenDrawer(false);
					}}
					slotProps={{
						paper: {
							sx: {
								justifyContent: 'space-between',
								bgcolor: 'common.white',
								width: '100%',
								height: 'calc(100% - 64px)',
								marginTop: '64px',
								boxShadow: 'none',
							},
						},
					}}
					sx={{
						height: 'calc(100% - 64px)',
						marginTop: '64px',
					}}
				>
					<List sx={{ padding: 0 }}>
						{enNavigationList.map((props, i) => (
							<ListItem
								key={i}
								sx={{
									height: 56,
									padding: 0,
									':hover .MuiListItemButton-root': {
										bgcolor: 'transparent',
										'.MuiTypography-root': {
											color: 'primary.main',
										},
									},
								}}
							>
								<ListItemButton
									LinkComponent={Link}
									{...props}
									sx={{
										justifyContent: 'space-between',
										height: '100%',
										paddingX: 3,
									}}
								>
									<Typography fontFamily={fonts.style.fontFamily} fontWeight={700} fontSize={18} color={pathname === props.href ? 'primary.main' : 'common.black'}>
										{props.title}
									</Typography>
									<ArrowRightIcon width={14} height={16} fill={variables.grey600} />
								</ListItemButton>
							</ListItem>
						))}
					</List>
					<Grid container width="100%" paddingX={3} paddingBottom={5}>
						<Button
							LinkComponent={Link}
							href="/"
							sx={{
								display: 'flex',
								alignItems: 'center',
								bgcolor: warmGrey['100'],
								fontSize: 16,
								fontWeight: 700,
								letterSpacing: -0.1,
								borderRadius: 99,
								padding: '12px 14px',
								':hover': {
									bgcolor: '#FDFDFD',
								},
							}}
						>
							<KrIcon />
							<Typography fontSize={16} fontWeight={700} marginLeft="4px" marginRight={1}>
								KR
							</Typography>
							<LaunchIcon fontSize="small" htmlColor="black" />
						</Button>
					</Grid>
				</Drawer>
			</Grid>
		</AppBar>
	);
}
